import { Button, Heading, Modal } from "@athena/forge";
import React from "react";
import { useTranslation } from "react-i18next";
import { logRemoveFactorInitiated } from "../../lib/amplitude-utils";

export interface EnrolledFactorProps {
  id: string;
  factorName: string;
  enrolledNumber?: string;
  deviceLabel?: string;
  icon: string;
  removeFactorHandler: (factorId: string) => Promise<void>;
  isLastFactor?: boolean;
}

const EnrolledFactor = ({
  id,
  factorName,
  enrolledNumber,
  deviceLabel,
  icon,
  removeFactorHandler,
  isLastFactor = false,
}: EnrolledFactorProps): React.JSX.Element => {
  const { t } = useTranslation();
  const [isDisabled, setIsDisabled] = React.useState(false);
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);
  const removeFactor = async (): Promise<void> => {
    setIsDisabled(true);
    logRemoveFactorInitiated({ factorName });
    try {
      await removeFactorHandler(id);
    } finally {
      setIsDisabled(false);
    }
  };
  const handleRemoveButtonClick = async (e: React.MouseEvent): Promise<void> => {
    e.preventDefault();
    if (isLastFactor) {
      setShowConfirmModal(true);
      return;
    }
    await removeFactor();
  };
  return (
    <div className="uiam-edit-factor-row">
      <div className="enroll-factor-row clearfix">
        <div className="enroll-factor-icon-container fe_u_margin--right-medium">
          <div className={`factor-icon enroll-factor-icon ${icon}`}></div>
        </div>
      </div>
      <div>
        <div className="uiam-enrolled-factor-container">
          <Heading
            headingTag="h3"
            variant="section"
            text={factorName}
            className="fe_u_font-size--default"
          />
          <Button
            text="Remove"
            variant="secondary"
            size="medium"
            onClick={handleRemoveButtonClick}
            disabled={isDisabled}
          />
        </div>
        {isLastFactor && (
          <Modal
            show={showConfirmModal}
            headerText={t(
              "managefactors.remove.confirmationmodal.headertext.number",
            )}
            alertType="attention"
            hideDividers={true}
            primaryButtonText={t(
              "managefactors.remove.confirmationmodal.primarybutton",
            )}
            footerSecondaryButtons={[
              {
                text: t("managefactors.remove.confirmationmodal.secondarybutton"),
                onClick: () => setShowConfirmModal(false),
              },
            ]}
            onHide={() => setShowConfirmModal(false)}
            onPrimaryClick={async () => {
              setShowConfirmModal(false);
              await removeFactor();
            }}
          >
            <p>{t("managefactors.remove.confirmationmodal.body")}</p>
          </Modal>
        )}
        {(enrolledNumber || deviceLabel) && (
          <ul className="fe_u_font-size--small fe_u_margin--left-small fe_u_margin--top-small">
            {enrolledNumber && (
              <li className="uiam-enrolled-factor-detail-list-item">
                <span className="fe_u_font-weight--semibold">
                  {t("managefactors.factor.enrollednumber") + " "}
                </span>
                {enrolledNumber}
              </li>
            )}
            {deviceLabel && (
              <li className="uiam-enrolled-factor-detail-list-item">
                <span className="fe_u_font-weight--semibold">
                  {t("managefactors.factor.enrolledmethod") + " "}
                </span>
                {deviceLabel}
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
};

export default EnrolledFactor;
